// frontend/src/pages/PricingPage.tsx
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import styles from "./PricingPage.module.css";

const PricingPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Back */}
        <Link to="/" className={styles.back}>
          <ArrowLeft className="w-4 h-4" />
          {t("common.back", "Retour")}
        </Link>

        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>{t("pricing.title", "Tarifs simples et transparents")}</h1>
          <p className={styles.subtitle}>
            {t("pricing.subtitle", "Commencez gratuitement. Passez à Pro quand vous êtes prêt.")}
          </p>
        </div>

        {/* Plans */}
        <div className={styles.plans}>
          {/* Free */}
          <div className={styles.planCard}>
            <div className={styles.planHeader}>
              <h2 className={styles.planName}>{t("pricing.free", "Free")}</h2>
              <div className={styles.planPrice}>
                <span className={styles.priceAmount}>0 €</span>
                <span className={styles.pricePeriod}>/{t("pricing.month", "mois")}</span>
              </div>
              <p className={styles.planDesc}>
                {t("pricing.freeDesc", "Pour démarrer et tester la plateforme.")}
              </p>
            </div>
            <ul className={styles.featureList}>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.event1", "1 événement actif")}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.clients30", "Max 30 clients / événement")}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.history7", "Historique 7 jours")}
              </li>
              <li className={styles.featureItemDisabled}>
                <span className={styles.mark} aria-hidden="true">×</span>
                {t("pricing.feature.whatsapp", "Notifications WhatsApp")}
              </li>
              <li className={styles.featureItemDisabled}>
                <span className={styles.mark} aria-hidden="true">×</span>
                {t("pricing.feature.branding", "Logo et couleurs personnalisés")}
              </li>
            </ul>
            <Link to="/admin/register" className={styles.btnSecondary}>
              {t("pricing.startFree", "Commencer gratuitement")}
            </Link>
          </div>

          {/* Pro */}
          <div className={`${styles.planCard} ${styles.planCardPro}`}>
            <div className={styles.planHeader}>
              <h2 className={styles.planName}>Pro</h2>
              <div className={styles.planPrice}>
                <span className={styles.priceAmount}>29 €</span>
                <span className={styles.pricePeriod}>/{t("pricing.month", "mois")}</span>
              </div>
              <p className={styles.planDesc}>
                {t("pricing.proDesc", "Pour les professionnels qui veulent tout débloquer.")}
              </p>
            </div>
            <ul className={styles.featureList}>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.eventsUnlimited", "Événements illimités")}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.clientsUnlimited", "Clients illimités")}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.historyFull", "Historique complet")}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.whatsapp", "Notifications WhatsApp")}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.branding", "Logo et couleurs personnalisés")}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.noPoweredBy", "Sans watermark")}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.mark} aria-hidden="true">—</span>
                {t("pricing.feature.trial", "14 jours d'essai gratuit")}
              </li>
            </ul>
            <Link to="/admin/register" className={styles.btnPrimary}>
              {t("pricing.startTrial", "Essayer 14 jours gratuits")}
            </Link>
            <p className={styles.trialNote}>
              {t("pricing.trialNote", "Sans engagement. Annulable à tout moment.")}
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className={styles.faq}>
          <h2 className={styles.faqTitle}>{t("pricing.faq.title", "Questions fréquentes")}</h2>
          <div className={styles.faqGrid}>
            {[
              {
                q: t("pricing.faq.q1", "Puis-je annuler à tout moment ?"),
                a: t("pricing.faq.a1", "Oui, sans frais ni engagement. L'abonnement reste actif jusqu'à la fin de la période."),
              },
              {
                q: t("pricing.faq.q2", "Que se passe-t-il après l'essai ?"),
                a: t("pricing.faq.a2", "Vous passez automatiquement au plan Free. Vos données sont conservées."),
              },
              {
                q: t("pricing.faq.q3", "Les paiements sont-ils sécurisés ?"),
                a: t("pricing.faq.a3", "Oui, les paiements sont gérés par Stripe, la référence mondiale en matière de paiements en ligne sécurisés."),
              },
              {
                q: t("pricing.faq.q4", "Y a-t-il des frais cachés ?"),
                a: t("pricing.faq.a4", "Non. 29 € / mois, c'est tout. Pas de frais par transaction, pas de surprises."),
              },
            ].map((item, i) => (
              <div key={i}>
                <h3 className={styles.faqQ}>{item.q}</h3>
                <p className={styles.faqA}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
